import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { api, json } from '../api/client'
import type { MagicRequested, Providers } from '../api/types'
import { Button, ErrorNote, Field, Logo } from '../components/ui'
import { useMe } from '../hooks/useMe'

const ERRORS: Record<string, string> = {
  google_denied: 'Google sign-in was cancelled.',
  google_failed: 'Google sign-in did not complete. Try again.',
  google_unavailable: 'Google sign-in is not available right now.',
}

export function SignInPage() {
  const [params] = useSearchParams()
  const returnTo = params.get('return_to') ?? '/studio'
  const error = params.get('error')
  const me = useMe()
  const providers = useQuery({ queryKey: ['auth-providers'], queryFn: () => api<Providers>('/auth/providers') })
  const [email, setEmail] = useState('')
  const request = useMutation({
    mutationFn: () => api<MagicRequested>('/auth/magic/request', { method: 'POST', body: json({ email, return_to: returnTo }) }),
  })

  if (me.data?.authenticated) return <Navigate to={returnTo} replace />
  const googleHref = `/api/v1/auth/google/start?return_to=${encodeURIComponent(returnTo)}`

  return (
    <div className="auth-page">
      <div className="auth-card card">
        <div className="card-body stack">
          <div className="brand brand-lg"><Logo size={28} /> indiefuture</div>
          <div>
            <h1>Log in</h1>
            <p className="muted">Stream on your own site, and run your community’s shows from one studio.</p>
          </div>
          {error && <ErrorNote message={ERRORS[error] ?? 'Sign-in failed. Try again.'} />}
          {providers.data?.google && (
            <a className="btn btn-primary btn-block" href={googleHref}>Continue with Google</a>
          )}
          {providers.isSuccess && !providers.data.google && !providers.data.email && (
            <p className="muted small">Log-in isn’t set up on this server yet.</p>
          )}
          {providers.data?.email && (
            <>
              {providers.data.google && <div className="divider"><span>or, in development, by email</span></div>}
              {request.data ? (
                <div className="notice">
                  {request.data.dev_link ? (
                    <>
                      Email delivery is off in development. <a href={request.data.dev_link}>Open your sign-in link</a>.
                    </>
                  ) : (
                    <>
                      Check <strong>{email}</strong> for a sign-in link. It works once and expires in 15 minutes.
                    </>
                  )}
                </div>
              ) : (
                <form
                  className="stack"
                  onSubmit={(event) => {
                    event.preventDefault()
                    request.mutate()
                  }}
                >
                  <Field label="Email">
                    <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </Field>
                  {request.error && <ErrorNote error={request.error} />}
                  <Button type="submit" block busy={request.isPending}>Email me a sign-in link</Button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
